import React, { useEffect, useState } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import {
  FhirResourceTypes,
  expandObservationSpacing,
  isPathologyReportHeading,
  stripSocialHistoryHeading
} from '@oh-vcp/components-common';
import EconsultLabResultLines from '../econsultLabResultLines/EconsultLabResultLines';
import EconsultPathologyReports from '../econsultPathologyReports/EconsultPathologyReports';
import EconsultReportBlocks from '../econsultReportBlocks/EconsultReportBlocks';

const StyledReportSection = styled(Box)(({ theme }) => ({
  borderColor: theme.palette.base.grey5,
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: 3,
  padding: 10
}));

const LineFormattedText = styled(Typography)({
  whiteSpace: 'pre-line'
});

export type ClinicalReportGroup = 'cumulativeProfile' | 'labReports' | 'encounters';

type EconsultClinicalReportsDetailsProps = {
  group?: ClinicalReportGroup;
};

type ReportSection = {
  key: string;
  labelKey: string;
  content: string;
};

const CUMULATIVE_PROFILE_SECTIONS = [
  { type: FhirResourceTypes.MedicationRequest, labelKey: 'ClinicalReports.medications' },
  { type: FhirResourceTypes.Condition, labelKey: 'ClinicalReports.condition' },
  { type: FhirResourceTypes.Procedure, labelKey: 'ClinicalReports.procedures' },
  {
    type: FhirResourceTypes.FamilyMemberHistory,
    labelKey: 'ClinicalReports.familyMemberHistory'
  },
  { type: FhirResourceTypes.AllergyIntolerance, labelKey: 'ClinicalReports.allergyIntolerance' },
  { type: FhirResourceTypes.Immunization, labelKey: 'ClinicalReports.immunizations' },
  { type: FhirResourceTypes.Observation, labelKey: 'ClinicalReports.observations' }
];

const LABORATORY_HEADINGS = ['Laboratory/Pathology', 'Laboratory'];
const PATHOLOGY_HEADINGS = ['Pathology'];
const PATHOLOGY_SECTION_KEY = 'pathology';
const LAB_SECTION_KEYS = ['laboratory'];

const LEGACY_LAB_REPORT_HEADINGS = ['Laboratory/Pathology', 'Diagnostic Report'];

const toBlocks = (content?: string): string[] =>
  content ? content.split('\n\n').filter((block) => block.trim()) : [];

const headingOf = (block: string) => block.split('\n')[0].trim();

const bodyOf = (block: string) => block.split('\n').slice(1).join('\n').trim();

const blocksUnderHeadings = (blocks: string[], headings: string[]) =>
  blocks
    .filter((block) => headings.includes(headingOf(block)))
    .map(bodyOf)
    .filter(Boolean)
    .join('\n');

const isPathologyBlock = (block: string) => {
  const heading = headingOf(block);
  return PATHOLOGY_HEADINGS.includes(heading) || isPathologyReportHeading(heading);
};

const pathologyBlocks = (blocks: string[]) => blocks.filter(isPathologyBlock).join('\n\n');

const blocksOutsideHeadings = (blocks: string[], headings: string[]) =>
  blocks
    .filter((block) => !headings.includes(headingOf(block)) && !isPathologyBlock(block))
    .join('\n\n');

const stripLegacyHeading = (content: string) => {
  const [firstBlock, ...rest] = content.split('\n\n');
  return LEGACY_LAB_REPORT_HEADINGS.includes(firstBlock.trim()) ? rest.join('\n\n') : content;
};

const buildLabReportSections = (referralContent: any): ReportSection[] => {
  const observationBlocks = toBlocks(referralContent[FhirResourceTypes.Observation]);
  const diagnosticReportContent = referralContent[FhirResourceTypes.DiagnosticReport];

  return [
    {
      key: 'laboratory',
      labelKey: 'ClinicalReports.laboratory',
      content: blocksUnderHeadings(observationBlocks, LABORATORY_HEADINGS)
    },
    {
      key: PATHOLOGY_SECTION_KEY,
      labelKey: 'ClinicalReports.pathology',
      content: pathologyBlocks(observationBlocks)
    },
    {
      key: FhirResourceTypes.DiagnosticReport,
      labelKey: 'ClinicalReports.diagnosticReports',
      content: diagnosticReportContent ? stripLegacyHeading(diagnosticReportContent) : ''
    }
  ].filter((section) => section.content);
};

const buildEncounterSections = (referralContent: any): ReportSection[] =>
  [
    {
      key: FhirResourceTypes.Encounter,
      labelKey: 'ClinicalReports.encounters',
      content: referralContent[FhirResourceTypes.Encounter] ?? ''
    }
  ].filter((section) => section.content);

const buildCumulativeProfileSections = (referralContent: any): ReportSection[] =>
  CUMULATIVE_PROFILE_SECTIONS.map(({ type, labelKey }) => ({
    key: type,
    labelKey,
    content:
      type === FhirResourceTypes.Observation
        ? expandObservationSpacing(
            stripSocialHistoryHeading(
              blocksOutsideHeadings(toBlocks(referralContent[type]), LABORATORY_HEADINGS)
            )
          )
        : referralContent[type]
  })).filter((section) => section.content);

export const hasLabReportSections = (referralContent: any) =>
  !!referralContent && buildLabReportSections(referralContent).length > 0;

export const hasEncounterSections = (referralContent: any) =>
  !!referralContent && buildEncounterSections(referralContent).length > 0;

export const hasCumulativeProfileSections = (referralContent: any) =>
  !!referralContent && buildCumulativeProfileSections(referralContent).length > 0;

const EconsultClinicalReportsDetails: React.FC<EconsultClinicalReportsDetailsProps> = ({
  group = 'cumulativeProfile'
}) => {
  const { t } = useTranslation();
  const { caseDetails } = useEconsultCaseDetailsContext();
  const [referralContent, setReferralContent] = useState<any>();

  useEffect(() => {
    if (caseDetails?.externalReferralContent) {
      setReferralContent(JSON.parse(caseDetails?.externalReferralContent));
    }
  }, [caseDetails?.externalReferralContent]);

  if (!referralContent) return null;

  const sectionsByGroup: Record<ClinicalReportGroup, () => ReportSection[]> = {
    labReports: () => buildLabReportSections(referralContent),
    encounters: () => buildEncounterSections(referralContent),
    cumulativeProfile: () => buildCumulativeProfileSections(referralContent)
  };

  const sections: ReportSection[] = sectionsByGroup[group]();

  const renderSectionContent = (key: string, content: string) => {
    if (key === PATHOLOGY_SECTION_KEY) return <EconsultPathologyReports content={content} />;
    if (LAB_SECTION_KEYS.includes(key)) return <EconsultLabResultLines content={content} />;
    if (key === FhirResourceTypes.DiagnosticReport) {
      return <EconsultReportBlocks content={content} headingLineCount={2} lineSpacing={1} />;
    }
    if (key === FhirResourceTypes.Encounter) {
      return (
        <EconsultReportBlocks
          content={content}
          boldNonNarrativeLines
          blockSpacing={5}
          sectionSpacing={3}
        />
      );
    }
    return (
      <LineFormattedText variant="body2" color="base.grey3">
        {content}
      </LineFormattedText>
    );
  };

  return (
    <>
      {sections.map(({ key, labelKey, content }, index) => (
        <React.Fragment key={key}>
          <Typography variant="h5" color="base.grey4" mb={1} mt={index === 0 ? 0 : 3}>
            {t(labelKey)}
          </Typography>
          <StyledReportSection>{renderSectionContent(key, content)}</StyledReportSection>
        </React.Fragment>
      ))}
    </>
  );
};

export default EconsultClinicalReportsDetails;
